import { MICRO_PANTALLAS, MicroPantalla, TOTAL_MACROS } from '@core/state/matricula.model';

/**
 * La forma de las URL del flujo. **Contrato, no rutas.**
 *
 * Aqui se fija como se escribe una posicion del flujo en la barra de
 * direcciones; las rutas hijas las registra cada spec cuando entrega su
 * pantalla (AC-13). Declararlas antes apuntaria a pantallas que no existen.
 *
 * Dos decisiones que gobiernan todo lo demas:
 *
 * - El macro-paso viaja **en base 1**: lo lee una persona, y `/matricula/2/eps`
 *   es el paso 2. El store lo cuenta desde cero y la conversion vive aqui.
 * - La micro-pantalla viaja **por nombre**, nunca por indice. El indice depende
 *   de si el deportista es menor —a un mayor le falta `acudiente`— y un `/0/3`
 *   guardado en un marcador significaria dos pantallas distintas segun la edad.
 */

/**
 * La eleccion de tramite, y el sitio al que se vuelve al abandonar uno.
 *
 * Es la raiz porque ahi vivia el menu que reemplaza: los enlaces y los codigos
 * QR que apuntan al inicio siguen sirviendo. Entra en el contrato para que el
 * `salir` del caparazon tenga a donde ir sin escribir `'/'` a mano en cada
 * pantalla que abandone un flujo (AC-10 de SPEC-004).
 */
export const RUTA_INICIO = '';

/** Raiz del flujo de matricula nueva. */
export const RUTA_MATRICULA = 'academy/matricula';

/**
 * Raiz del flujo de renovacion. **Entregada por SPEC-015 el 2026-09-07.**
 *
 * La tarjeta *"Ya estudio aquí"* del inicio apunta aqui desde SPEC-004 (AC-4), y
 * hasta esta spec caia en el comodin y devolvia al inicio. Declararla antes fue
 * lo que permitio que las dos specs coincidieran en la URL sin que una esperara
 * a la otra.
 *
 * **Su ruta lleva guard** —`renovacionActiva`, el primero del proyecto—: con
 * `renewalModuleIsActive` apagado el tramite es presencial, y la direccion
 * escrita a mano no puede entrar donde la tarjeta ya no lleva.
 */
export const RUTA_RENOVACION = 'academy/renovacion';

/**
 * La ruta por la que se vuelve al tramite desde el correo. Es la de la
 * aplicacion actual y **no se cambia**: hay enlaces vivos apuntando ahi.
 */
export const RUTA_REENTRANTE = 'academy/registration/new-registration/:id';

/**
 * La misma ruta, ya resuelta con el id de una solicitud.
 *
 * Vive aqui y no en quien navega porque la escriben dos sitios: el envio, que
 * manda a la persona a su radicado recien creado, y el enlace del correo. Que
 * los dos compongan la direccion a mano seria tener el contrato en dos partes.
 */
export function enlaceDeSeguimiento(id: string): string[] {
  return ['/', ...RUTA_REENTRANTE.replace('/:id', '').split('/'), id];
}

/**
 * La eleccion de sede, ya aprobada la solicitud (SPEC-011).
 *
 * Cuelga de la ruta reentrante y no de `RUTA_MATRICULA`: se llega por el enlace
 * del correo, con el id de la solicitud, y no por el caparazon del formulario.
 * Es la misma razon por la que el seguimiento vive donde vive.
 */
export const RUTA_SEDE = `${RUTA_REENTRANTE}/sede`;

export function enlaceDeSede(id: string): string[] {
  return [...enlaceDeSeguimiento(id), 'sede'];
}

/**
 * La eleccion de horario (SPEC-011).
 *
 * **Lleva el ciclo y la sede en la direccion, y no en un store.** Los grupos se
 * consultan por el `id` del ciclo y se filtran por sede: sin los dos, una
 * recarga no sabria que ensenar. El segmento del ciclo es ese `id` y no su
 * `seasonId` — son campos distintos y solo el primero lo referencian los grupos
 * (`idDeCiclo` en `features/registro/ciclos.ts`). Es lo que SPEC-021 dejo escrito —la direccion sigue a la
 * pantalla— y de paso hace que el boton de atras del navegador signifique
 * *"cambiar de sede"*, que es lo que la persona espera.
 */
export const RUTA_HORARIO = `${RUTA_REENTRANTE}/horario/:ciclo/:sede`;

export function enlaceDeHorario(id: string, ciclo: string, sede: string): string[] {
  return [...enlaceDeSeguimiento(id), 'horario', ciclo, sede];
}

/**
 * El resumen de pago (SPEC-012).
 *
 * No lleva ciclo ni sede en la direccion: para cuando se llega aqui la eleccion
 * **ya esta guardada** en la solicitud, asi que el id basta y una recarga la
 * recupera entera.
 */
export const RUTA_PAGO = `${RUTA_REENTRANTE}/pago`;

export function enlaceDePago(id: string): string[] {
  return [...enlaceDeSeguimiento(id), 'pago'];
}

/**
 * El retorno de la pasarela (SPEC-013).
 *
 * **Es la unica ruta del flujo que Academy no escoge.** La compone
 * `buildPaymentFlowControl` como `responseUrl` —es a donde PayU devuelve al
 * terminar— y es tambien a donde enlaza el informe de legalizaciones del portal
 * de gestion. De ahi que sus segmentos vayan en ingles: son los de la
 * aplicacion actual y hay enlaces vivos apuntando ahi.
 *
 * Hasta esta spec **caia en el comodin** y acababa en el inicio: las dos
 * entradas, la de la pasarela y la del portal, se perdian igual.
 *
 * No lleva `enlaceDe...`: desde dentro de Academy no navega nadie hasta aqui.
 * Si algun dia cambia `RUTA_REENTRANTE`, cambia con ella la direccion que
 * `PaymentFlowControl` compone a mano — y las dos tienen que seguir diciendo lo
 * mismo.
 */
export const RUTA_RETORNO_PAGO = `${RUTA_REENTRANTE}/payment/response`;

/** Una posicion del flujo tal y como la escribe la URL. */
export interface PosicionEnRuta {
  macro: number;
  pantalla: MicroPantalla | null;
}

/**
 * El enlace a un paso, para el `routerLink` del `Editar` del paso 4. Sin
 * micro-pantalla apunta al macro-paso, que es lo que se ve en modo amplio.
 */
export function enlaceDePaso(macro: number, pantalla?: MicroPantalla): string[] {
  const enlace = ['/', ...RUTA_MATRICULA.split('/'), String(macro + 1)];
  return pantalla ? [...enlace, pantalla] : enlace;
}

/**
 * Lo contrario: los parametros de la URL de vuelta a una posicion, o `null` si
 * no describen ninguna real. Una URL escrita a mano no puede dejar el flujo en
 * un paso que no existe, ni en una pantalla que pertenece a otro macro-paso.
 */
export function posicionDeRuta(
  macro: string | null,
  pantalla: string | null,
): PosicionEnRuta | null {
  const numero = Number(macro);
  if (!macro || !Number.isInteger(numero) || numero < 1 || numero > TOTAL_MACROS) return null;

  const indiceMacro = numero - 1;
  if (!pantalla) return { macro: indiceMacro, pantalla: null };

  const pantallas = MICRO_PANTALLAS[indiceMacro] ?? [];
  const nombre = pantallas.find((candidata) => candidata === pantalla);
  return nombre ? { macro: indiceMacro, pantalla: nombre } : null;
}

/**
 * El recorrido de una renovacion, ya con el alumno identificado (SPEC-016).
 *
 * **Cuelga del `memberId` y no de una solicitud**, porque una renovacion no
 * tiene: viaja sobre una `Membership`. Ese id es ademas opaco — el documento de
 * un menor no tiene por que quedar escrito en la barra de direcciones, que es el
 * mismo criterio con el que SPEC-015 recorto la ficha.
 *
 * **Y lo elegido viaja en la direccion hasta el final**, que es la otra
 * diferencia con la matricula nueva. Alli la eleccion se guarda en la solicitud
 * en cuanto se hace, y por eso el resumen de pago solo necesita el id. Aqui no
 * hay donde guardarla: la membresia **nace con el pago** —antes no tendria
 * `paymentId` que consultar, y la Logic App la borraria a los 20 minutos— asi
 * que hasta entonces la lleva la URL. Una recarga sigue funcionando (Regla 4).
 */
export function enlaceDeRenovacion(): string[] {
  return ['/', ...RUTA_RENOVACION.split('/')];
}

export const RUTA_SEDE_RENOVACION = `${RUTA_RENOVACION}/:miembro/sede`;

export function enlaceDeSedeRenovacion(miembro: string): string[] {
  return [...enlaceDeRenovacion(), miembro, 'sede'];
}

export const RUTA_HORARIO_RENOVACION = `${RUTA_RENOVACION}/:miembro/horario/:ciclo/:sede`;

export function enlaceDeHorarioRenovacion(miembro: string, ciclo: string, sede: string): string[] {
  return [...enlaceDeRenovacion(), miembro, 'horario', ciclo, sede];
}

/**
 * El resumen de pago de una renovacion. **Lleva el ciclo y el grupo**, que es lo
 * que en matricula nueva ya esta guardado en la solicitud.
 */
export const RUTA_PAGO_RENOVACION = `${RUTA_RENOVACION}/:miembro/pago/:ciclo/:grupo`;

export function enlaceDePagoRenovacion(miembro: string, ciclo: string, grupo: string): string[] {
  return [...enlaceDeRenovacion(), miembro, 'pago', ciclo, grupo];
}

/**
 * El retorno de la pasarela de una renovacion (AC-5).
 *
 * **No se escoge**: la compone `buildPaymentFlowControl` con el origen
 * `renewal-registration`, que el dominio del pago ya declaraba desde la
 * migracion — y hasta esta spec **no la atendia nadie**, asi que la vuelta caia
 * en el comodin y acababa en el inicio. Es el mismo defecto que SPEC-013
 * arreglo para la matricula nueva.
 *
 * Su `:id` no es el de la membresia: es el `uuid` que viaja como
 * `administrativeData.applicationId`, y es por el que la pantalla la encuentra.
 */
export const RUTA_RETORNO_RENOVACION = 'academy/registration/renewal-registration/:id/payment/response';

/**
 * La misma ruta, **relativa** a `RUTA_RENOVACION`: es como la registra el
 * enrutador hijo de la renovacion, que cuelga entero de esa raiz para que el
 * guard `renovacionActiva` proteja tambien las pantallas de dentro.
 */
export function bajoRenovacion(ruta: string): string {
  return ruta.slice(RUTA_RENOVACION.length + 1);
}
